import { useState, useEffect, useMemo, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { api, adminApi } from "@/lib/api";
import { toast } from "sonner";
import { Eye, EyeOff, X, Mail, Lock } from "lucide-react";

/**
 * Modal dialog to update the admin credentials (email and/or password) from
 * within the console. Requires the current password as an authorization gate.
 * At least one of email OR password must actually change.
 * On success, the backend rotates the HttpOnly session cookie so the caller
 * stays signed in with the new identity — nothing is written to localStorage.
 *
 * (Filename kept as ChangePasswordDialog.jsx for import stability; component is
 *  now semantically an UpdateCredentialsDialog.)
 */
export default function ChangePasswordDialog({ open, onClose, onUpdated }) {
  const [currentEmail, setCurrentEmail] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);

  const strength = useMemo(() => scorePassword(next), [next]);

  const reset = useCallback(() => {
    setNewEmail(currentEmail);
    setCurrent(""); setNext(""); setConfirm(""); setShowPw(false);
  }, [currentEmail]);

  const close = () => {
    reset();
    onClose && onClose();
  };

  // Fetch the current admin email so the "New email" field is prefilled.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setProfileLoaded(false);
      try {
        const me = await adminApi.me();
        if (cancelled) return;
        const email = me?.email || "";
        setCurrentEmail(email);
        setNewEmail(email);
      } catch (err) {
        console.warn("[admin] Could not load admin profile:", err?.message || err);
      } finally {
        if (!cancelled) setProfileLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const emailIsChanging = newEmail.trim().toLowerCase() !== currentEmail.trim().toLowerCase();
  const passwordIsChanging = next.length > 0 || confirm.length > 0;

  const submit = async (e) => {
    e.preventDefault();

    if (!emailIsChanging && !passwordIsChanging) {
      return toast.error("Change email, password, or both — nothing new to save.");
    }
    if (passwordIsChanging) {
      if (next !== confirm) return toast.error("New password and confirmation do not match.");
      if (next.length < 10) return toast.error("New password must be at least 10 characters.");
      if (!/[a-zA-Z]/.test(next) || !/[0-9]/.test(next)) return toast.error("New password must contain a letter and a number.");
      if (current === next) return toast.error("New password must be different from current password.");
    }
    if (emailIsChanging) {
      // Very light client-side format check; server does the authoritative check.
      const trimmed = newEmail.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
        return toast.error("Please enter a valid email address.");
      }
    }
    if (!current) return toast.error("Enter your current password to confirm.");

    setBusy(true);
    try {
      const body = { current_password: current };
      if (emailIsChanging) body.new_email = newEmail.trim().toLowerCase();
      if (passwordIsChanging) body.new_password = next;
      const r = await api.post("/admin/update-credentials", body);
      // Cookie is rotated server-side. Nothing to store client-side.
      const changed = r.data?.changed || [];
      const msg = changed.length === 2
        ? "Email and password updated. You're still signed in."
        : changed.includes("email")
          ? `Email changed to ${r.data.email}. You're still signed in.`
          : "Password changed. You're still signed in.";
      toast.success(msg);
      onUpdated && onUpdated(r.data);
      close();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update credentials.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.22 }}
          className="fixed inset-0 z-[80] bg-black/55 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={close}
          data-testid="update-credentials-overlay"
        >
          <motion.form
            onSubmit={submit}
            onClick={(e) => e.stopPropagation()}
            initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.2, 0.8, 0.2, 1] }}
            className="w-full max-w-[480px] rounded-2xl bg-[#1A2232] border border-[#26334D] p-5 sm:p-6 shadow-[0_20px_60px_rgba(0,0,0,0.55)]"
            role="dialog"
            aria-labelledby="update-creds-title"
          >
            <div className="flex items-center justify-between">
              <h2 id="update-creds-title" className="text-lg font-semibold text-[#F8FAFC]">Update admin credentials</h2>
              <button type="button" onClick={close} aria-label="Close" className="h-8 w-8 rounded-lg text-[#94A3B8] hover:bg-[#232E45] hover:text-[#F8FAFC] inline-flex items-center justify-center">
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-1 text-xs text-[#94A3B8]" style={{ lineHeight: 1.6 }}>
              Change your admin email, password, or both. Both are gated by your <span className="text-[#F8FAFC]">current password</span>.
            </p>

            {/* Email section */}
            <label className="mt-5 flex items-center gap-1.5 text-xs font-semibold tracking-wide text-[#94A3B8] uppercase">
              <Mail className="h-3.5 w-3.5" aria-hidden /> Admin email
            </label>
            <input
              data-testid="update-creds-email"
              type="email"
              autoComplete="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder={profileLoaded ? "you@example.com" : "loading…"}
              className="mt-2 h-11 w-full rounded-xl border border-[#26334D] bg-[#111622] px-3 text-sm text-[#F8FAFC] placeholder-[#64748B] focus:outline-none focus:ring-2 focus:ring-[#E85A2A]"
            />
            {emailIsChanging && (
              <p className="mt-1.5 text-[11px] text-[#F2BD68]" style={{ lineHeight: 1.5 }}>
                Your sign-in email will change from <span className="font-semibold">{currentEmail}</span> to <span className="font-semibold">{newEmail.trim().toLowerCase()}</span>.
              </p>
            )}

            {/* Divider */}
            <div className="my-5 h-px bg-[#26334D]" aria-hidden />

            {/* Password section */}
            <label className="flex items-center gap-1.5 text-xs font-semibold tracking-wide text-[#94A3B8] uppercase">
              <Lock className="h-3.5 w-3.5" aria-hidden /> Current password <span className="text-[#E85A2A] normal-case">*required</span>
            </label>
            <div className="relative">
              <input
                data-testid="update-creds-current"
                type={showPw ? "text" : "password"}
                autoComplete="current-password"
                required
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                className="mt-2 h-11 w-full rounded-xl border border-[#26334D] bg-[#111622] px-3 pr-10 text-sm text-[#F8FAFC] focus:outline-none focus:ring-2 focus:ring-[#E85A2A]"
              />
              <button type="button" onClick={() => setShowPw(!showPw)} aria-label={showPw ? "Hide passwords" : "Show passwords"} className="absolute right-2 top-1/2 mt-1 -translate-y-1/2 h-8 w-8 rounded-lg text-[#94A3B8] hover:bg-[#232E45] hover:text-[#F8FAFC] inline-flex items-center justify-center">
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>

            <label className="mt-4 block text-xs font-semibold tracking-wide text-[#94A3B8] uppercase">New password <span className="text-[#94A3B8] normal-case font-normal">(leave blank to keep current)</span></label>
            <input
              data-testid="update-creds-new"
              type={showPw ? "text" : "password"}
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="mt-2 h-11 w-full rounded-xl border border-[#26334D] bg-[#111622] px-3 text-sm text-[#F8FAFC] focus:outline-none focus:ring-2 focus:ring-[#E85A2A]"
            />
            {next && <StrengthMeter score={strength.score} label={strength.label} />}

            {passwordIsChanging && (
              <>
                <label className="mt-4 block text-xs font-semibold tracking-wide text-[#94A3B8] uppercase">Confirm new password</label>
                <input
                  data-testid="update-creds-confirm"
                  type={showPw ? "text" : "password"}
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="mt-2 h-11 w-full rounded-xl border border-[#26334D] bg-[#111622] px-3 text-sm text-[#F8FAFC] focus:outline-none focus:ring-2 focus:ring-[#E85A2A]"
                />
                {confirm && next !== confirm && (
                  <p className="mt-1 text-xs text-[#B42318]">Passwords do not match.</p>
                )}

                <ul className="mt-4 space-y-1 text-xs text-[#94A3B8]" style={{ lineHeight: 1.6 }}>
                  <li className={next.length >= 10 ? "text-[#7DE3B6]" : ""}>• At least 10 characters</li>
                  <li className={/[a-zA-Z]/.test(next) && /[0-9]/.test(next) ? "text-[#7DE3B6]" : ""}>• Contains letters and numbers</li>
                  <li className={next && current && current !== next ? "text-[#7DE3B6]" : ""}>• Different from current password</li>
                </ul>
              </>
            )}

            <div className="mt-6 flex items-center gap-2">
              <button type="button" onClick={close} className="flex-1 rounded-xl border border-[#26334D] bg-[#1A2232] px-4 py-2.5 text-sm font-semibold text-[#F8FAFC] hover:bg-[#232E45] transition-colors">Cancel</button>
              <button
                data-testid="update-creds-submit"
                type="submit"
                disabled={busy || (!emailIsChanging && !passwordIsChanging)}
                className="flex-1 rounded-xl bg-[#E85A2A] text-[#F8FAFC] px-4 py-2.5 text-sm font-semibold hover:bg-[#C94A1D] transition-colors disabled:opacity-50 shadow-[0_8px_24px_rgba(232,90,42,0.30)]"
              >
                {busy ? "Updating…" : emailIsChanging && passwordIsChanging ? "Update both" : emailIsChanging ? "Update email" : "Change password"}
              </button>
            </div>
          </motion.form>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function scorePassword(pw) {
  if (!pw) return { score: 0, label: "" };
  let s = 0;
  if (pw.length >= 10) s += 1;
  if (pw.length >= 14) s += 1;
  if (/[A-Z]/.test(pw)) s += 1;
  if (/[a-z]/.test(pw)) s += 1;
  if (/[0-9]/.test(pw)) s += 1;
  if (/[^A-Za-z0-9]/.test(pw)) s += 1;
  const labels = ["Very weak", "Weak", "Weak", "Okay", "Good", "Strong", "Excellent"];
  return { score: s, label: labels[Math.min(s, 6)] };
}

function StrengthMeter({ score, label }) {
  const pct = Math.min(100, (score / 6) * 100);
  const color = score >= 5 ? "#7DE3B6" : score >= 3 ? "#E85A2A" : score >= 2 ? "#F2BD68" : "#B42318";
  return (
    <div className="mt-2">
      <div className="h-1.5 w-full rounded-full bg-[#26334D] overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <p className="mt-1 text-[11px] font-semibold" style={{ color }}>{label}</p>
    </div>
  );
}
